import { useState, useEffect } from "react";
import { fetchData, fetchRelatedLinks, buildBigQueryUrl } from "../data/https";

export const useIndicatorData = (isVisible, url, info, countryCode, itemId, country, dataSource, comparingCountries) => {
    const [data, setData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [relatedLinks, setRelatedLinks] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetchComplete, setFetchComplete] = useState(false);

    useEffect(() => {
        if (!isVisible) return undefined;

        // Switching country re-runs this effect while the previous queries are still in
        // flight. Without aborting them they keep a slot in the request queue and delay the
        // data the user is actually waiting for.
        const controller = new AbortController();

        const loadData = async () => {
            setIsLoading(true);
            setError(null);
            setFetchComplete(false);

            const fetchDataUrl = buildBigQueryUrl(url);

            try {
                const relatedLinksUrl = `https://data.fao.org/api/v2/bigquery?query=SELECT%20*%20FROM%20fao-maps-review.fao_cca.dim_documents%20WHERE%20domain_code%20%3D%20%22${dataSource}%22&output_format=json&download=false`;
                //const relatedLinksUrl = `https://data.review.fao.org/bigquery?query=SELECT%20*%20FROM%20fao-maps-review.fao_cca.dim_documents%20WHERE%20domain_code%20%3D%20%22${dataSource}%22&output_format=json&download=false`;
                // Built fresh rather than pushed onto info.metadata_blacklist: that array
                // lives in the shared indicator config and grew a duplicate entry per render.
                const blacklist = [...(info?.metadata_blacklist || []), 'download_template_e.html'];
                const [fetchedData, fetchedRelatedLinks] = await Promise.all([
                    fetchData(fetchDataUrl, info, countryCode, comparingCountries, controller.signal),
                    fetchRelatedLinks(relatedLinksUrl, blacklist),
                ]);
                if (controller.signal.aborted) return;
                setData(fetchedData.transformed);
                setOriginalData(fetchedData.originalData);
                setRelatedLinks(fetchedRelatedLinks);
            } catch (error) {
                // An abort means a newer request has taken over: it owns the state now.
                if (controller.signal.aborted || error?.name === "AbortError") return;
                setError(`Error: Failed to fetch data — ${error?.message || "Unknown error"}`);
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                    setFetchComplete(true);
                }
            }
        };

        loadData();

        return () => controller.abort();
    }, [isVisible, url, info, itemId, country, countryCode, dataSource, comparingCountries]);

    return {
        data,
        originalData,
        relatedLinks,
        isLoading,
        error,
        fetchComplete
    };
};
